"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HostInCall = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const sdk_core_1 = require("@streem/sdk-core");
const room_session_1 = require("../context/room_session");
const company_settings_1 = require("../context/company_settings");
const host_call_frame_1 = require("../components/in_call/host/host_call_frame");
const __1 = require("..");
const redirect_helpers_1 = require("../util/redirect_helpers");
const company_info_1 = require("../context/company_info");
const ui_react_1 = require("@streem/ui-react");
const Fullscreen = ui_react_1.styled.div(({ theme }) => `
        z-index: ${theme.layers.callFrame};
        display: flex;
        width: 100%;
    `);
function HostInCall({ roomSession, companySettings, roomInvitationId, companyInfo, groupReservationId, exitToLobby = () => { }, }) {
    if (!(0, sdk_core_1.isRoomSession)(roomSession)) {
        throw new sdk_core_1.StreemError('Unable to provide in-call experience without a RoomSession.');
    }
    const theme = (0, ui_react_1.useTheme)();
    const [roomInvitationStatus] = (0, __1.useObservable)(roomSession.roomInvitationStatus);
    const CompanyInfoProvider = (0, react_1.useMemo)(() => {
        return (0, company_info_1.createCompanyInfoProvider)(companyInfo);
    }, [companyInfo]);
    const RoomSessionProvider = (0, react_1.useMemo)(() => {
        return (0, room_session_1.createRoomSessionProvider)(roomSession);
    }, [roomSession]);
    const CompanySettingsProvider = (0, react_1.useMemo)(() => {
        return (0, company_settings_1.createCompanySettingsProvider)(companySettings);
    }, [companySettings]);
    const handleRedirectToCustomerRedirectUrl = (statusCode) => {
        var _a;
        // The value for this company setting is hardcoded to true for streem-now
        if (companySettings === null || companySettings === void 0 ? void 0 : companySettings.endRedirectDisabled) {
            return;
        }
        (0, __1.customerRedirectUrlHandler)(companySettings === null || companySettings === void 0 ? void 0 : companySettings.endRedirectUrl, (_a = roomSession === null || roomSession === void 0 ? void 0 : roomSession.roomId) === null || _a === void 0 ? void 0 : _a.toString(), groupReservationId === null || groupReservationId === void 0 ? void 0 : groupReservationId.toString(), statusCode);
    };
    const onCallEndedByHost = () => {
        /* There are two possible scenarios that need to be handled when the host ends the call:
         * 1. The host has ended the call after the spectator has accepted the room invitation and the room is
         *    connected. This case should result with a status code of OK.
         * 2. The host has ended the call before the spectator has accepted the room invitation.
         *    This case should result with a status code of Canceled.
         */
        let redirectStatusCode = redirect_helpers_1.CustomerRedirectUrlStatuses.OK;
        /* TODO: https://streempro.atlassian.net/browse/ST-3786
         * The room invitation status is expected to be CANCELED when the host ends the call before the spectator
         * accepts the room invitation but it is currently reading as PENDING.
         */
        if (roomInvitationStatus === 'PENDING') {
            redirectStatusCode = redirect_helpers_1.CustomerRedirectUrlStatuses.CALL_CANCELED;
        }
        handleRedirectToCustomerRedirectUrl(redirectStatusCode);
    };
    const onRoomInvitationNotAccepted = (roomInvitationStatus) => {
        /* When using group reservation the host should be redirected to the customer redirect url
         * instead of exiting to the lobby, so they can be matched with another available spectator.
         */
        if (groupReservationId) {
            if (roomInvitationStatus === 'REJECTED') {
                handleRedirectToCustomerRedirectUrl(redirect_helpers_1.CustomerRedirectUrlStatuses.CALL_REJECTED);
            }
            if (roomInvitationStatus === 'TIMED_OUT') {
                handleRedirectToCustomerRedirectUrl(redirect_helpers_1.CustomerRedirectUrlStatuses.CALL_TIMED_OUT);
            }
        }
        else {
            exitToLobby();
        }
    };
    const onRoomDisconnected = (roomStatus) => {
        if (roomStatus === 'DISCONNECTED') {
            handleRedirectToCustomerRedirectUrl(redirect_helpers_1.CustomerRedirectUrlStatuses.OK);
        }
    };
    return ((0, jsx_runtime_1.jsx)(Fullscreen, Object.assign({ theme: theme, className: "InCallFullscreen" }, { children: (0, jsx_runtime_1.jsx)(RoomSessionProvider, { children: (0, jsx_runtime_1.jsx)(CompanySettingsProvider, { children: (0, jsx_runtime_1.jsxs)(CompanyInfoProvider, { children: [(0, jsx_runtime_1.jsx)(InvitationWatcher, { onRoomInvitationNotAccepted: onRoomInvitationNotAccepted, onRoomDisconnected: onRoomDisconnected, roomInvitationId: roomInvitationId }), (0, jsx_runtime_1.jsx)(host_call_frame_1.HostCallFrame, { onCallEndedByHost: onCallEndedByHost })] }) }) }) })));
}
exports.HostInCall = HostInCall;
const InvitationWatcher = ({ roomInvitationId, onRoomInvitationNotAccepted, onRoomDisconnected, }) => {
    var _a;
    const roomSession = (0, room_session_1.useRoomSession)();
    const [roomInvitation] = (0, __1.useObservable)(roomSession.roomInvitation);
    const invitationIdString = roomInvitation === null || roomInvitation === void 0 ? void 0 : roomInvitation.id.toString();
    const roomInvitationStatus = (_a = roomInvitation === null || roomInvitation === void 0 ? void 0 : roomInvitation.status) !== null && _a !== void 0 ? _a : 'UNKNOWN';
    const roomInvitationIdString = roomInvitationId === null || roomInvitationId === void 0 ? void 0 : roomInvitationId.toString();
    const [roomStatus] = (0, __1.useObservable)(roomSession.status);
    (0, react_1.useEffect)(() => {
        // handle roomInvitation id mismatch race condition to prevent premature "REJECTED" status confirmation
        if (roomInvitation &&
            invitationIdString === roomInvitationIdString &&
            ['REJECTED', 'TIMED_OUT'].includes(roomInvitationStatus)) {
            onRoomInvitationNotAccepted(roomInvitationStatus);
        }
    }, [
        invitationIdString,
        roomInvitationIdString,
        roomInvitationStatus,
        onRoomInvitationNotAccepted,
        roomInvitation,
    ]);
    (0, react_1.useEffect)(() => {
        if (roomStatus === 'DISCONNECTED') {
            onRoomDisconnected(roomStatus);
        }
    }, [roomStatus, onRoomDisconnected]);
    return null;
};
//# sourceMappingURL=host_in_call.js.map